import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role constants - Phase 6.5: Added cashier and inventory_manager
export const ROLES = {
  OWNER: 'owner',
  DOCTOR: 'doctor',
  NURSE: 'nurse',
  RECEPTION: 'reception',
  CASHIER: 'cashier',
  INVENTORY_MANAGER: 'inventory_manager'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setForcePasswordChange(response.data.force_password_change || false);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, force_password_change } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setForcePasswordChange(force_password_change || false);
    return userData;
  };

  const register = async (email, password, full_name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, full_name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setForcePasswordChange(false);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setForcePasswordChange(false);
  };

  // Role-based access helpers - Phase 6.5: Updated for cashier and inventory manager
  const permissions = useMemo(() => ({
    // Can access billing (invoices, payments, revenue) - Owner, Reception, Cashier
    canAccessBilling: user?.role === ROLES.OWNER || user?.role === ROLES.CASHIER,
    // Can access clinical data (encounters detail, notes) - Owner, Doctor, Nurse (Nurse is read-only)
    canAccessClinical: user?.role === ROLES.OWNER || user?.role === ROLES.DOCTOR || user?.role === ROLES.NURSE,
    // Can edit clinical data (encounters, notes) - Only Owner and Doctor
    canEditClinical: user?.role === ROLES.OWNER || user?.role === ROLES.DOCTOR,
    // Can manage patients (all roles except cashier and inventory manager)
    canManagePatients: ![ROLES.CASHIER, ROLES.INVENTORY_MANAGER].includes(user?.role),
    // Is owner/admin
    isOwner: user?.role === ROLES.OWNER,
    // Is doctor
    isDoctor: user?.role === ROLES.DOCTOR,
    // Is nurse
    isNurse: user?.role === ROLES.NURSE,
    // Is reception
    isReception: user?.role === ROLES.RECEPTION,
    // Is cashier (Phase 6.5)
    isCashier: user?.role === ROLES.CASHIER,
    // Is inventory manager (Phase 6.5)
    isInventoryManager: user?.role === ROLES.INVENTORY_MANAGER,
    // Is staff (not owner)
    isStaff: user?.role !== ROLES.OWNER,
    // Can view inventory (Phase 6.5)
    canViewInventory: user?.role === ROLES.OWNER || user?.role === ROLES.INVENTORY_MANAGER,
    // Current role
    role: user?.role || null
  }), [user?.role]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, forcePasswordChange, setForcePasswordChange, ...permissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
